import { memo, useState, useEffect, useMemo } from "react";
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { Grid2, Button, FormControl, FormLabel, TextField, Typography, Select, Alert, MenuItem, Box, AlertTitle, FormHelperText } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import { useCommonStyles } from '../../styles/GenericStyle';
import { useForm, Controller } from "react-hook-form";
import { useGlobalState } from "../../contexts/GlobalState";
import type { CreateSPLotModel, CreateSuccessSPLotDataModel } from '../../api/vegApl/SPLotModel';
import { useCreateSPLot } from '../../api/vegApl';
import { useQueryClient } from "@tanstack/react-query"
import { white, syngentaGreen, red, greyDark } from "../../colors";
import { MtComStyleObj } from '../../styles/MtComStyleObj';
import { useExportToExcel } from '../../utils/ExcelExport';
import { useSnackbar } from '../../contexts/SnackbarContext';
import { GenericConstant } from '../../utils/GenericConstant';
import AddIcon from '@mui/icons-material/Add';
import PrintOutlinedIcon from '@mui/icons-material/PrintOutlined';
import KeyboardArrowDownOutlinedIcon from '@mui/icons-material/KeyboardArrowDownOutlined';
import { useDropdownPosition } from '../../hooks/useDropdownPosition';
import PrintSPLotLabelDialog from '../common/PrintSPLotLabelDialog';
import { usePrintLabel } from '../../hooks/usePrintLabel';
import  {numericOnlyKeyDown} from '../../utils/HelperFunction';
import type { LabelTemplate } from '../../api/vegApl/labelTemplate';
import FullScreenActionLoader from '../common/FullScreenActionLoader';
export type SPLotDialogprops = {
    open: boolean,
    setOpenSPLotDialog: (open: boolean) => void
    setOpenAddMat: (open: boolean) => void
    setDrillDownCurrRow: (data: any) => void
    setRefreshSPLotGrid: (open: boolean) => void
    onCreated?: (splotIds: string[]) => void
    splotTemplates?: LabelTemplate[]
    preferredCropId?: string
    preferredCropName?: string
}

function SPLotFormDialog({
    open,
    setOpenSPLotDialog,
    setOpenAddMat,
    setDrillDownCurrRow,
    setRefreshSPLotGrid,
    onCreated,
    splotTemplates = [],
    preferredCropId,
    preferredCropName,
}: SPLotDialogprops) {
    const classes = useCommonStyles();
    const { crops, state: globalState, state: { siteSelected } } = useGlobalState();
    const [SPLotAddedSuccess, setSPLotAddedSuccess] = useState(true);
    const queryClient = useQueryClient();
    const { exportToExcel } = useExportToExcel();
    const [isLoading, setIsLoading] = useState(false);
    const { showSnackbar } = useSnackbar();
    const { menuPosition, calculatePosition } = useDropdownPosition(300);
    const [generatedSPLotRes, setGeneratedSPLotRes] = useState<CreateSuccessSPLotDataModel | null>(null);

    const {
        printLabelOpen, printLabelRow, hasTemplate, foundTemplate, isPrinting,
        openPrintDialog, closePrintDialog, bulkPrint,
    } = usePrintLabel({ templateType: 'Splot', defaultSite: siteSelected || 'MAAG' });

    const handlePrintLabel = async () => {
        const splotIds = generatedSPLotRes?.splot_ids ?? [];
        if (splotIds.length === 0) return;

        const cropCode = getValues('crop_id');
        const site = siteSelected || 'MAAG';

        if (splotIds.length === 1) {
            openPrintDialog({ splot_id: splotIds[0], crop: cropCode, site_code: site }, site, cropCode);
            return;
        }

        const rows = splotIds.map((id: string) => ({ splot_id: id, crop: cropCode, site_code: site }));
        await bulkPrint(rows, site, cropCode);
    };
    const {
        control, register, handleSubmit, reset, getValues, setValue, formState: { errors },
    } = useForm<CreateSPLotModel>({
        defaultValues: { crop_id: "", site_cd: "", no_of_splot: 1, user_id: 0, comments: "" },
        mode: "onChange"
    });

    const resolvedPreferredCropId = useMemo(() => {
        if (preferredCropId && crops.some((crop) => crop?.crop_xrd_oid === preferredCropId)) {
            return preferredCropId;
        }

        if (preferredCropName) {
            const normalizedPreferredCropName = preferredCropName.trim().toLowerCase();
            const matchedCrop = crops.find((crop) => (crop?.name ?? "").trim().toLowerCase() === normalizedPreferredCropName);
            if (matchedCrop?.crop_xrd_oid) {
                return matchedCrop.crop_xrd_oid;
            }
        }

        return "";
    }, [preferredCropId, preferredCropName, crops]);
    const onSubmit = (formData: CreateSPLotModel) => {
        formData.user_id = globalState?.user_id_db;
        formData.site_cd = siteSelected;
        setIsLoading(true);
        createSPLot({ data: formData })
    }
    const exportSPLot = () => {
        let arr = generatedSPLotRes ? generatedSPLotRes?.splot_ids?.map((id: any) => ({ "SP Lot ID": id })) : [];
        exportToExcel(arr, "spLot", [])
    }
    const { mutate: createSPLot } = useCreateSPLot({
        mutation: {
            onSuccess: (data: any) => {
                setIsLoading(false);
                setGeneratedSPLotRes(data?.data);
                setSPLotAddedSuccess(true);
                queryClient.invalidateQueries({ queryKey: ["spLots"] });
                queryClient.invalidateQueries({ queryKey: ["kpi-processing-metrics"] });
                queryClient.invalidateQueries({ queryKey: ["kpi-trial-processing-metrics"] });
                setRefreshSPLotGrid(true);
                onCreated?.(data?.data?.splot_ids ?? []);
            },
            onError: (error: any) => {
                setIsLoading(false);
                const detail = error?.response?.data?.detail;
                const errorMessage = typeof detail === 'string' ? detail : 'Error in creating SPLot';
                showSnackbar({ message: errorMessage, severity: 'error' });
                showSnackbar({ message: errorMessage, severity: 'error' });

            },
        },
    })

    useEffect(() => {
        setGeneratedSPLotRes(null);
        setSPLotAddedSuccess(false);
        reset({
            crop_id: resolvedPreferredCropId,
            site_cd: "",
            no_of_splot: 1,
            user_id: 0,
            comments: "",
        });
    }, [open, reset, resolvedPreferredCropId]);

    const handleItemClick = (item: string) => {

        if (getValues("crop_id") == item) {
            setValue("crop_id", "");
        }
    }

    return (
        <>
            <FullScreenActionLoader open={isLoading} />
            <Dialog open={open}
                slotProps={{
                    paper: {
                        sx: {
                            borderRadius: "16px",
                        },
                    },
                }}
                sx={(theme) => ({
                    ...MtComStyleObj(theme).dialogBoxStyle
                })}>
                <DialogTitle sx={{ pt: 0, pb: 0 }}>
                    <Box display={"flex"} justifyContent={"space-between"}>
                        <div>
                            <Typography variant="h6" component="div" className={classes.labelNoto14500}>Create SP Lot</Typography>
                            <Typography variant="body2" className={classes.labelNoto16Bold400}>{SPLotAddedSuccess ? "SP Lot(s) successfully generated" : "Enter SP Lot Details"}</Typography>
                        </div>
                        <IconButton aria-label="close" onClick={() => setOpenSPLotDialog(false)} sx={(theme) => ({ ...MtComStyleObj(theme).dialogCloseButton })}><CloseIcon /></IconButton>
                    </Box>
                </DialogTitle>
                <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
                    <DialogContent >
                        {SPLotAddedSuccess && <Alert severity={"success"}
                            icon={
                                <Box
                                    component="img"
                                    src="icons/tick_success_icon.svg"
                                    alt=""
                                    sx={{ width: 24, height: 24, display: 'block' }}
                                />
                            }

                            sx={{
                                p: "18px 30px", mb: 2, borderRadius: "4px",
                                backgroundColor: "#EFFFED",
                                border: `0.1px solid #D1D5DC`,
                                '& .MuiAlert-icon': {
                                    color: syngentaGreen, fontSize: 24, mr: 1.5, alignItems: 'center', display: 'flex',
                                },
                            }}>
                            <AlertTitle>
                                <Typography component="div" className={classes.labelNoto16Bold600} color={syngentaGreen} >SP Lot Created Successfully</Typography>
                                <Typography component="div" className={classes.labelInter14Bold400}>{generatedSPLotRes?.splot_ids?.length ?? ""} SP Lots generated</Typography>
                            </AlertTitle>
                        </Alert>}
                        {!SPLotAddedSuccess ? <Grid2 container spacing={2}>
                            <Grid2 size={{ xs: 12, sm: 6, md: 12 }}>
                                <FormControl size="small" fullWidth>
                                    <FormLabel sx={{ color: greyDark, '&.Mui-focused': { color: greyDark } }} className={classes.labelInter14Bold400}>Crop <span style={{ color: red, marginLeft: 4 }}>*</span></FormLabel>
                                    <Controller
                                        name="crop_id" control={control} rules={{ required: "Please select crop" }}
                                        render={({ field }) => (
                                            <Select
                                                IconComponent={KeyboardArrowDownOutlinedIcon}
                                                sx={(theme) => ({
                                                    height: 40, mt: 1,
                                                    ...MtComStyleObj(theme).fieldFocusHoverStyle,
                                                    "& .MuiSelect-icon": {
                                                        fontSize: 25,
                                                        color: '#000000'
                                                    }
                                                })}
                                                {...field}
                                                displayEmpty
                                                onOpen={calculatePosition}
                                                renderValue={(selected) => {
                                                    return selected ? <Typography component="span" className={classes.labelNoto16Bold400}>
                                                        {crops.find((ele) => ele?.crop_xrd_oid == selected)?.name}
                                                    </Typography> : <Typography component="span" className={classes.labelNoto16Bold400} color="#808080">Choose a Crop</Typography>
                                                }}
                                                value={field.value || ""} MenuProps={{
                                                    anchorOrigin: menuPosition.anchorOrigin,
                                                    transformOrigin: menuPosition.transformOrigin,
                                                    PaperProps: {
                                                        style: {
                                                            maxHeight: 300,
                                                            marginTop: menuPosition.anchorOrigin.vertical === "top" ? -8 : 8
                                                        }
                                                    }
                                                }}>
                                                {crops?.map((crop, index) => (
                                                    <MenuItem disableRipple sx={{ ...GenericConstant.DROPDOWNSTYLE }} key={'spLotCrops' + index} value={crop?.crop_xrd_oid ?? ""} onClick={() => handleItemClick(crop?.crop_xrd_oid)}>{crop?.name}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        )}
                                    />
                                    <FormHelperText sx={{ ml: 0 }} className={classes.errorText}>{errors?.crop_id?.message}</FormHelperText>
                                </FormControl>
                            </Grid2>
                            <Grid2 size={{ xs: 12, sm: 6, md: 12 }}>
                                <FormControl size="small" fullWidth>
                                    <FormLabel sx={{ color: greyDark }} className={classes.labelInter14Bold400}>Number of SP Lots<span style={{ color: red, marginLeft: 4 }}>*</span></FormLabel>
                                    <TextField placeholder="Enter Number" type="number"
                                        onKeyDown={numericOnlyKeyDown}
                                        sx={(theme) => ({
                                            mt: 1,
                                            ...MtComStyleObj(theme).textFocusHoverStyle,
                                            ...MtComStyleObj(theme).textBoxValueStyle,
                                        })}  {...register('no_of_splot', {
                                            required: 'Please enter number of SpLots', max: { value: 20, message: 'Maximum allowed is 20.' }, min: { value: 1, message: 'Number of SP lots must be at least 1.' },
                                        })} />
                                    <FormHelperText sx={{ ml: 0 }} className={classes.errorText}>{errors?.no_of_splot?.message}</FormHelperText>
                                </FormControl>
                            </Grid2>
                            <Grid2 size={{ xs: 12, sm: 6, md: 12 }}>
                                <FormControl size="small" fullWidth>
                                    <FormLabel sx={{ color: greyDark }} className={classes.labelInter14Bold400}>Comments</FormLabel>
                                    <TextField sx={(theme) => ({
                                        mt: 1,
                                        ...MtComStyleObj(theme).textFocusHoverStyle,
                                        ...MtComStyleObj(theme).textBoxValueStyle,
                                    })} placeholder="Enter Comment" autoComplete="off"
                                        {...register('comments', {
                                        })} />
                                </FormControl>
                            </Grid2>
                        </Grid2>
                            : <>
                                <Typography component="span" sx={{ color: greyDark }} className={classes.labelNoto14Bold400}>Generated SP Lot ID</Typography>
                                {generatedSPLotRes?.splot_ids.map((spId: any) => (
                                    <Box className={classes.labelNoto16Bold400} sx={{ mt: 1, padding: "8px 16px", borderRadius: "4px", height: 40, width: "100%", backgroundColor: white, border: "1px solid #D1D5DC", color: "#808080" }} >{spId}</Box>))}

                                <Alert severity="info" icon={false}
                                    sx={{
                                        pt: 0, mt: 2,
                                        backgroundColor: "#ECF2FD", borderRadius: "4px",
                                        border: `0.1px solid #D1D5DC`
                                    }}>
                                    <AlertTitle color="text.primary" className={classes.labelNoto16Bold400}>Details</AlertTitle>
                                    <Box sx={{ display: "flex", flexDirection: "column" }}>
                                        <Typography component="div">
                                            <Typography component="span" color="text.secondary" className={classes.labelInter14Bold400} >Site : </Typography>
                                            <Typography component="span" color="text.primary" className={classes.labelNoto14Bold700} >{siteSelected}</Typography>
                                        </Typography>
                                        <Typography component="div">
                                            <Typography component="span" color="text.secondary" className={classes.labelInter14Bold400} >Crop : </Typography>
                                            <Typography component="span" color="text.primary" className={classes.labelNoto14Bold700} >{generatedSPLotRes?.crop_name}</Typography>
                                        </Typography>
                                        <Typography component="div">
                                            <Typography component="span" color="text.secondary" className={classes.labelInter14Bold400} >Count : </Typography>
                                            <Typography component="span" color="text.primary" className={classes.labelNoto14Bold700} >{generatedSPLotRes?.no_of_splots}</Typography>
                                        </Typography>
                                    </Box>
                                </Alert> </>
                        }
                    </DialogContent>
                    <DialogActions>

                        {!SPLotAddedSuccess ? <>

                            <Button variant="contained" disableRipple sx={{ padding: "10px 12px" }} className={classes.whiteButton} onClick={() => { setOpenSPLotDialog(false) }}>Cancel</Button>
                            <Button type="submit" variant="contained" sx={{ padding: "10px 12px" }} disableRipple loading={isLoading} loadingPosition="start" className={classes.greenButton}>Create</Button></>
                            : <>
                                <Button variant="contained" disableRipple sx={{ padding: "10px 12px" }} className={classes.whiteButton} onClick={() => { setOpenSPLotDialog(false) }}>Close</Button>
                                <Button variant="contained" disableRipple sx={{ padding: "10px 12px" }} className={classes.whiteButton} onClick={() => exportSPLot()}>Download</Button>
                                <Button variant="contained" disableRipple sx={{ padding: "10px 12px" }} className={classes.whiteButton} onClick={() => handlePrintLabel()} loading={isPrinting} startIcon={<PrintOutlinedIcon />}>Print Label</Button>
                                {generatedSPLotRes?.splot_ids.length == 1 && <Button variant="contained" sx={{ padding: "10px 12px" }} disableRipple className={classes.greenButton} onClick={() => {
                                    const selectedCropCode = getValues('crop_id');
                                    const selectedCropName = crops.find((crop: any) => crop?.crop_xrd_oid === selectedCropCode)?.name ?? "";
                                    setOpenAddMat(true),
                                        setOpenSPLotDialog(false),
                                        setDrillDownCurrRow({
                                            "splot_id": generatedSPLotRes?.splot_ids?.[0] ?? "",
                                            "crop": selectedCropCode,
                                            "crop_code": selectedCropCode,
                                            "crop_name": selectedCropName,
                                            "site_code": siteSelected
                                        })
                                }} startIcon={<AddIcon />}> Add Material</Button>}
                            </>}

                    </DialogActions>
                </Box>
            </Dialog>

            <PrintSPLotLabelDialog
                open={printLabelOpen}
                onClose={closePrintDialog}
                onPrint={(templateName) => {
                    console.log('Print label with template:', templateName, printLabelRow);
                    closePrintDialog();
                }}
                rowData={printLabelRow ?? {}}
                hasTemplate={hasTemplate}
                foundTemplate={foundTemplate}
                splotTemplates={splotTemplates}
            />
        </>
    );
}
export default memo(SPLotFormDialog);